import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Message, SettingsCard, Tooltip } from "@humansignal/ui";
import { useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { cn } from "../../utils/bem";
import { askConfirm } from "../../utils/ask";
import { Loading } from "../../components/Loading/Loading";
import { Pagination, usePage, usePageSize } from "../../components/Pagination/Pagination";
import "./TypedLayersSettings.prefix.css";

const block = cn("typed-layers");
const el = (name) => block.elem(name).toClassName();

const KIND_OPTIONS = [
  { value: "span", label: "Span (attach to text spans / labels)" },
  { value: "relation", label: "Relation (between two spans)" },
  { value: "chain", label: "Chain (coreference / grouping)" },
  { value: "document", label: "Document-level" },
];

const DTYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "boolean", label: "Boolean" },
  { value: "integer", label: "Integer" },
  { value: "float", label: "Float" },
  { value: "link", label: "Link (URL)" },
  { value: "concept_kb_ref", label: "Concept, pick from Knowledge Base" },
];

const emptyLayer = { name: "", kind: "span", description: "" };
const emptyFeature = {
  name: "",
  dtype: "string",
  required: false,
  knowledge_base: null,
  default: "",
  description: "",
  display_order: 0,
};

// ── Layer form ────────────────────────────────────────────────────────────

const LayerForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial ?? emptyLayer);
  useEffect(() => setForm(initial ?? emptyLayer), [initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <form className={el("form")} onSubmit={submit}>
      <div className={el("form-row")}>
        <label>Name</label>
        <input value={form.name} onChange={set("name")} required maxLength={128} placeholder="e.g. gene_mention" />
      </div>
      <div className={el("form-row")}>
        <label>Kind</label>
        <select value={form.kind} onChange={set("kind")}>
          {KIND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className={el("form-row")}>
        <label>Description</label>
        <textarea rows={2} value={form.description} onChange={set("description")} />
      </div>
      <div className={el("form-actions")}>
        <Button type="button" variant="negative" look="outlined" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="small" disabled={saving || !form.name.trim()}>
          {saving ? "Saving…" : initial?.id ? "Save layer" : "Create layer"}
        </Button>
      </div>
    </form>
  );
};

// ── Feature form ──────────────────────────────────────────────────────────

const FeatureForm = ({ initial, kbs, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial ?? emptyFeature);
  useEffect(() => setForm(initial ?? emptyFeature), [initial]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const payload = { ...form };
    // Backend rejects a KB pointer on non-KB features and requires one on KB features.
    if (payload.dtype !== "concept_kb_ref") payload.knowledge_base = null;
    else if (!payload.knowledge_base) return;
    onSave(payload);
  };

  const kbOptions = kbs ?? [];

  return (
    <form className={el("feature-form")} onSubmit={submit}>
      <div className={el("form-row-grid")}>
        <div className={el("form-row")}>
          <label>Feature name</label>
          <input value={form.name} onChange={set("name")} required maxLength={128} placeholder="e.g. tui" />
        </div>
        <div className={el("form-row")}>
          <label>Type</label>
          <select value={form.dtype} onChange={set("dtype")}>
            {DTYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {form.dtype === "concept_kb_ref" && (
        <div className={el("form-row")}>
          <label>Knowledge Base</label>
          {kbOptions.length === 0 ? (
            <Message look="warning">
              No knowledge bases yet. Create one under Organization → Settings → Knowledge Bases first.
            </Message>
          ) : (
            <select value={form.knowledge_base ?? ""} onChange={set("knowledge_base")} required>
              <option value="">— Select a KB —</option>
              {kbOptions.map((kb) => (
                <option key={kb.id} value={kb.id}>
                  {kb.name} ({kb.entry_count} entr{kb.entry_count === 1 ? "y" : "ies"})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className={el("form-row-grid")}>
        <label className={el("checkbox-row")}>
          <input type="checkbox" checked={form.required} onChange={set("required")} />
          <span>Required</span>
        </label>
        {["string", "integer", "float", "boolean", "link"].includes(form.dtype) && (
          <div className={el("form-row")}>
            <label>Default</label>
            <input value={form.default ?? ""} onChange={set("default")} placeholder="" />
          </div>
        )}
      </div>

      <div className={el("form-row")}>
        <label>Description</label>
        <textarea rows={2} value={form.description} onChange={set("description")} />
      </div>

      <div className={el("form-actions")}>
        <Button type="button" variant="negative" look="outlined" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="small" disabled={saving}>
          {saving ? "Saving…" : initial?.id ? "Save feature" : "Add feature"}
        </Button>
      </div>
    </form>
  );
};

// ── Feature row (read-only) ───────────────────────────────────────────────

const dtypeLabel = (dtype) => DTYPE_OPTIONS.find((o) => o.value === dtype)?.label ?? dtype;

const FeatureRow = ({ feature, kbs, onEdit, onDelete }) => {
  const kb = feature.knowledge_base ? (kbs ?? []).find((k) => k.id === feature.knowledge_base) : null;
  return (
    <tr>
      <td className={el("mono")}>{feature.name}</td>
      <td>
        {dtypeLabel(feature.dtype)}
        {feature.dtype === "concept_kb_ref" && kb && <span className={el("kb-ref")}> → {kb.name}</span>}
      </td>
      <td>{feature.required ? "Yes" : "No"}</td>
      <td className={el("description")}>{feature.description || <span className={el("muted")}>—</span>}</td>
      <td className={el("row-actions")}>
        <Button variant="neutral" size="smaller" look="outlined" onClick={() => onEdit(feature)}>
          Edit
        </Button>
        <Button size="smaller" look="outlined" variant="negative" onClick={() => onDelete(feature)}>
          Delete
        </Button>
      </td>
    </tr>
  );
};

// ── Layer card ────────────────────────────────────────────────────────────

const LayerCard = ({
  layer,
  kbs,
  onEditLayer,
  onDeleteLayer,
  onAddFeature,
  onEditFeature,
  onDeleteFeature,
  editingFeature,
  featureSaving,
  onSaveFeature,
  onCancelFeature,
}) => {
  // Page state keyed by layer id — several LayerCards render on one
  // page, so each needs its own URL param / stored page size.
  const [page, setPage] = usePage(`tlf${layer.id}`, 1);
  const [pageSize, setPageSize] = usePageSize(`tlf${layer.id}_size`, 10);
  return (
    <SettingsCard>
      <div className={el("layer-header")}>
        <div>
          <h4 className={el("layer-title")}>
            <span className={el("mono")}>{layer.name}</span>
            <span
              className={block
                .elem("layer-kind")
                .mod({ [layer.kind]: true })
                .toClassName()}
            >
              {layer.kind}
            </span>
            <span className={el("id-chip")} title="Use this ID in the TypedFeature tag's layer= attribute">
              layer={layer.id}
            </span>
          </h4>
          {layer.description && <p className={el("muted")}>{layer.description}</p>}
        </div>
        <div className={el("layer-actions")}>
          <Button size="small" look="outlined" onClick={() => onEditLayer(layer)}>
            Edit layer
          </Button>
          <Button size="small" look="outlined" variant="negative" onClick={() => onDeleteLayer(layer)}>
            Delete layer
          </Button>
        </div>
      </div>

      <div className={el("layer-toolbar")}>
        <Button size="small" variant="primary" onClick={() => onAddFeature(layer)}>
          + Add feature
        </Button>
      </div>

      {editingFeature && editingFeature._layerId === layer.id && (
        <SettingsCard title={editingFeature.id ? `Edit "${editingFeature.name}"` : "New feature"}>
          <FeatureForm
            initial={editingFeature}
            kbs={kbs}
            saving={featureSaving}
            onSave={(payload) => onSaveFeature(layer, payload)}
            onCancel={onCancelFeature}
          />
        </SettingsCard>
      )}

      {!layer.features || layer.features.length === 0 ? (
        <Message look="info">No features on this layer yet.</Message>
      ) : (
        <>
          <table className={el("features")}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...(layer.features ?? [])]
                .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name))
                .slice((page - 1) * pageSize, page * pageSize)
                .map((f) => (
                  <FeatureRow
                    key={f.id}
                    feature={f}
                    kbs={kbs}
                    onEdit={(feat) => onEditFeature({ ...feat, _layerId: layer.id })}
                    onDelete={(feat) => onDeleteFeature(layer, feat)}
                  />
                ))}
            </tbody>
          </table>
          <Pagination
            name={`typed-layer-features-${layer.id}`}
            page={page}
            pageSize={pageSize}
            totalItems={layer.features.length}
            urlParamName={`tlf${layer.id}`}
            pageSizeOptions={[10, 25, 50]}
            onPageLoad={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
            style={{ paddingTop: 12 }}
          />
        </>
      )}
    </SettingsCard>
  );
};

// ── Page root ─────────────────────────────────────────────────────────────

const TypedLayersView = () => {
  const api = useAPI();
  const toast = useToast();
  const { project } = useContext(ProjectContext);

  const [layers, setLayers] = useState(null);
  const [kbs, setKbs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // layer being created/edited or null
  const [layerSaving, setLayerSaving] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null); // feature carrying _layerId
  const [featureSaving, setFeatureSaving] = useState(false);

  const projectId = project?.id;

  const loadLayers = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const res = await api.callApi("typedLayers", { params: { pk: projectId } });
    setLoading(false);
    if (res?.error) {
      toast.show({ message: res.error || "Failed to load layers", type: "error" });
      return;
    }
    const list = Array.isArray(res) ? res : (res?.results ?? []);
    setLayers(list);
  }, [api, projectId, toast]);

  const loadKbs = useCallback(async () => {
    const res = await api.callApi("knowledgeBases");
    if (res?.error) return;
    const list = Array.isArray(res) ? res : (res?.results ?? []);
    setKbs(list);
  }, [api]);

  useEffect(() => {
    loadLayers();
    loadKbs();
  }, [projectId]); // eslint-disable-line

  const saveLayer = useCallback(
    async (form) => {
      if (!projectId) return;
      setLayerSaving(true);
      const res = editing?.id
        ? await api.callApi("typedLayerUpdate", {
            params: { pk: projectId, layer_pk: editing.id },
            body: form,
          })
        : await api.callApi("typedLayerCreate", {
            params: { pk: projectId },
            body: form,
          });
      setLayerSaving(false);
      if (res?.error) {
        toast.show({ message: res.error || "Save failed", type: "error" });
        return;
      }
      toast.show({ message: editing?.id ? "Layer updated" : "Layer created" });
      setEditing(null);
      await loadLayers();
    },
    [api, editing, loadLayers, projectId, toast],
  );

  const deleteLayer = useCallback(
    async (layer) => {
      if (!projectId) return;
      if (
        !(await askConfirm({
          title: "Delete layer?",
          body: `"${layer.name}" and all its features will be deleted.`,
        }))
      )
        return;
      const res = await api.callApi("typedLayerDelete", {
        params: { pk: projectId, layer_pk: layer.id },
      });
      if (res?.error) {
        toast.show({ message: res.error || "Delete failed", type: "error" });
        return;
      }
      toast.show({ message: "Layer deleted" });
      await loadLayers();
    },
    [api, loadLayers, projectId, toast],
  );

  const saveFeature = useCallback(
    async (layer, form) => {
      if (!projectId) return;
      setFeatureSaving(true);
      const params = { pk: projectId, layer_pk: layer.id };
      const payload = { ...form };
      delete payload._layerId;
      const res = editingFeature?.id
        ? await api.callApi("typedFeatureUpdate", {
            params: { ...params, feature_pk: editingFeature.id },
            body: payload,
          })
        : await api.callApi("typedFeatureCreate", { params, body: payload });
      setFeatureSaving(false);
      if (res?.error) {
        toast.show({ message: res.error || "Save failed", type: "error" });
        return;
      }
      setEditingFeature(null);
      await loadLayers();
    },
    [api, editingFeature, loadLayers, projectId, toast],
  );

  const deleteFeature = useCallback(
    async (layer, feature) => {
      if (!projectId) return;
      if (
        !(await askConfirm({
          title: "Delete feature?",
          body: `Remove feature "${feature.name}" from layer "${layer.name}"?`,
        }))
      )
        return;
      const res = await api.callApi("typedFeatureDelete", {
        params: { pk: projectId, layer_pk: layer.id, feature_pk: feature.id },
      });
      if (res?.error) {
        toast.show({ message: res.error || "Delete failed", type: "error" });
        return;
      }
      await loadLayers();
    },
    [api, loadLayers, projectId, toast],
  );

  const startAddFeature = useCallback((layer) => {
    setEditingFeature({ ...emptyFeature, _layerId: layer.id });
  }, []);

  return (
    <div className={block.toClassName()}>
      <SettingsPageHeader title="Typed feature layers" />

      <SettingsCard
        title="Overview"
        description={
          <>
            Attach typed metadata to your annotations — span-level attributes (e.g. UMLS type), relation properties, or
            document-level features. Concept references pick from a Knowledge Base defined at the organization level.
            This slice defines the schema; annotation UI in slice 3 renders form controls per feature.
          </>
        }
      >
        <div className={el("toolbar")}>
          <Button size="small" variant="primary" onClick={() => setEditing({ ...emptyLayer })}>
            + New layer
          </Button>
        </div>
      </SettingsCard>

      {editing !== null && (
        <SettingsCard title={editing.id ? `Edit "${editing.name}"` : "New layer"}>
          <LayerForm initial={editing} saving={layerSaving} onSave={saveLayer} onCancel={() => setEditing(null)} />
        </SettingsCard>
      )}

      {loading && layers === null ? (
        <Loading />
      ) : (layers ?? []).length === 0 ? (
        <Message look="info">No layers defined yet. Create one to attach typed metadata to your annotations.</Message>
      ) : (
        (layers ?? []).map((layer) => (
          <LayerCard
            key={layer.id}
            layer={layer}
            kbs={kbs}
            onEditLayer={setEditing}
            onDeleteLayer={deleteLayer}
            onAddFeature={startAddFeature}
            onEditFeature={setEditingFeature}
            onDeleteFeature={deleteFeature}
            editingFeature={editingFeature}
            featureSaving={featureSaving}
            onSaveFeature={saveFeature}
            onCancelFeature={() => setEditingFeature(null)}
          />
        ))
      )}
    </div>
  );
};

export const TypedLayersSettings = {
  title: "Typed layers",
  path: "/typed-layers",
  component: TypedLayersView,
};

export default TypedLayersView;
